import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';

import { Link } from 'core/routing';
import { routerStore } from 'core';
import { Username } from 'apps/components/shared';
import { Avatar, Button, Tooltip } from 'components';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';
import {
  PostCard,
  TextWrapper,
  FullWidthTitle as Title,
  PostCardActions,
  pseudoCardStyles,
} from 'legacyComponents/cards/styles';
import { Date } from 'apps/components/date';

import { clientStore } from 'apps/client/app';
import {
  isInDesktopMode,
  openUrlInBrowser,
} from 'libs/gutClient/electronBridge';

export const ContributionCard = withNamespaces()(
  observer(({ cardObject, cardNo, cardColumns, cwdFiles }) => {
    const noMargin = (cardNo + 1) % cardColumns === 0;
    if (cardObject === null) {
      return <CardIndicator noMargin={noMargin} type='issue' />;
    }

    const {
      title,
      creatorProfile,
      version,
      zipArchiveUrl,
      content,
      dateCreated,
      id,
    } = cardObject;

    const currentItem = cwdFiles?.find(
      ({ contributionFile }) => contributionFile?.origin?.id === id,
    );

    const shouldShowDownload = currentItem?.contributionFile?.file?.canDownload;

    const versionRoute = `${clientStore.spaceRoute.route}.version.files`;
    const contribRoute = `${clientStore.spaceRoute.route}.contribution`;
    const shortVersion = version.substring(0, 7);
    const params = Object.assign({}, clientStore.spaceParams, {
      contributionId: shortVersion,
    });

    const handleTimeTravelClick = event => {
      if (isInDesktopMode()) {
        const relativeUrl = routerStore.router.buildUrl(versionRoute, params);
        openUrlInBrowser(`${window.location.origin}${relativeUrl}`);
        event.preventDefault();
      }
    };

    return (
      <PostCard data-intercom-target={cardNo === 0 ? 'project-history' : ''}>
        <Link
          name='profile'
          params={{ profile: creatorProfile.username }}
          content={creatorProfile.content}
        >
          <Avatar
            src={creatorProfile.avatar?.url}
            name={creatorProfile.fullName}
            size='xmedium'
          />
        </Link>
        <TextWrapper>
          <Link name={contribRoute} params={params} content={content}>
            <Title>{title || `Contribution #${shortVersion}`}</Title>
          </Link>
          <ContributionInfoWrapper>
            <LinkContribution name={contribRoute} params={params}>
              (#{shortVersion})
            </LinkContribution>
            <Trans>contributed by </Trans>
            <Username
              name='profile'
              params={{ profile: creatorProfile.username }}
              content={creatorProfile.content}
            >
              {creatorProfile.username}
            </Username>
            <DateStyled value={dateCreated} />
          </ContributionInfoWrapper>
        </TextWrapper>
        <PostCardActions>
          <Tooltip delayShow={500} />
          <Link
            name={versionRoute}
            params={params}
            content={content}
            onClick={handleTimeTravelClick}
          >
            <Button
              variant='secondary'
              icon='time_travel'
              ariaLabel='Files'
              data-tip='Browse the Drive at this point in history'
              outline
            />
          </Link>
          {shouldShowDownload && (
            <Button
              variant='secondary'
              icon='download'
              ariaLabel='Download'
              as='a'
              target='_blank'
              href={zipArchiveUrl}
              rel='noopener noreferrer'
              data-tip='Download the Drive at this point in history'
              download
              outline
            />
          )}
        </PostCardActions>
      </PostCard>
    );
  }),
);

const LinkContribution = styled(Link)`
  color: ${props => props.theme.color.wfGrey700};
  text-decoration: underline;
  &:hover {
    text-decoration: none;
  }
  &::after {
    ${pseudoCardStyles}
  }
`;

const DateStyled = styled(Date)`
  &::before {
    ${pseudoCardStyles}
  }
`;

const ContributionInfoWrapper = styled.div`
  margin: 0;
  font-size: ${props => props.theme.font.size.small};
  font-weight: ${props => props.theme.font.weight.regular};
  line-height: ${props => props.theme.font.lineHeight.medium};
  color: ${props => props.theme.color.wfBlack};
`;

ContributionCard.propTypes = {
  avatar: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string,
  author: PropTypes.string,
  datePublished: PropTypes.string,
  comments: PropTypes.array,
  imageFallbackPath: PropTypes.string,
};
